import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    output,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import {NavigationStart, Router, RouterLink} from '@angular/router';
import {
    CheckboxGroupComponent,
    InputNumberGroupComponent,
    InputSelectOptionGroupComponent,
    InputTextGroupComponent,
    LoadComponent,
} from '@brunata-angular/ui';
import {
    AppValidators,
    filterNulls,
    fromControl,
    TRANSLATE,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {Store} from '@ngrx/store';
import {SelectableItem} from '@brunata-angular/ui';
import {HasUnsavedChanges} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable} from 'rxjs';
import {selectBuilding} from '../../../state/selectors';
import {ConsumptionAgentService} from '../consumption-agent.service';
import {
    ConsumptionAgentFormGroup,
    ConsumptionAgentRequest,
    createUpdateAgentRequest,
    mapFormGroupToConsumptionAgentTranslated,
} from '../consumption-agents-overview/consumption-agent.model';
import {ConsumptionAgentInvestigationComponent} from './consumption-agent-investigation/consumption-agent-investigation.component';
import {
    ConsumptionAgentOperator,
    ConsumptionAgentSchedule,
    ConsumptionAgentType,
    IncreaseDecreaseAgentComparisonPeriod,
} from './consumption-agent-investigation/consumption-agent-investigation.model';
import {ConsumptionAgentMonitoringComponent} from './consumption-agent-monitoring/consumption-agent-monitoring.component';
import {ConsumptionToMonitor} from './consumption-agent-monitoring/consumption-agent-monitoring.model';
import {AgentMeterType} from './select-meters-for-agent/select-meters-for-agent.model';
import {SelectMetersForAgentService} from './select-meters-for-agent/select-meters-for-agent.service';
import {InputEmailListComponent} from '@brunata-angular/ui';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PreviewResultModalComponent} from './preview-result-modal/preview-result-modal.component';

type CreateConsumptionAgentView = {
    formGroup: ConsumptionAgentFormGroup;
    buildingNo: string;
    schedulerMap: Map<ConsumptionAgentSchedule, string>;
    outputUnitTranslated: string;
};

@Component({
    selector: 'app-create-consumption-agent',
    standalone: true,
    imports: [
        TranslatePipe,
        CommonModule,
        RouterLink,
        LoadComponent,
        ReactiveFormsModule,
        ConsumptionAgentInvestigationComponent,
        ConsumptionAgentMonitoringComponent,
        InputEmailListComponent,
        InputTextGroupComponent,
        InputNumberGroupComponent,
        CheckboxGroupComponent,
        InputSelectOptionGroupComponent,
    ],
    templateUrl: './create-consumption-agent.component.html',
    styleUrl: './create-consumption-agent.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateConsumptionAgentComponent implements HasUnsavedChanges, OnInit, OnDestroy {
    @Input() initialFormValues: ConsumptionAgentFormGroup['value'] | null = null;
    @Output() agentRequest = new EventEmitter<ConsumptionAgentRequest>();

    unsavedChanges = output<boolean>();

    private readonly store = inject(Store);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly router = inject(Router);
    private readonly toastrService = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);
    private readonly translateService = inject(TranslateService);
    private readonly consumptionAgentService = inject(ConsumptionAgentService);
    private readonly metersForAgentService = inject(SelectMetersForAgentService);
    private readonly modalService = inject(NgbModal);

    private readonly schedulerMap = new Map<ConsumptionAgentSchedule, string>([
        ['DISABLED', this.translate('utilities_no_notification')],
        ['DAILY_8AM', this.translate('utilities_every_day')],
        ['WEEKLY_EVERY_MONDAY_8AM', this.translate('utilities_every_monday')],
        ['BIMONTHLY_EVERY_SECOND_MONDAY_8AM', this.translate('utilities_every_2_weeks')],
        ['MONTHLY_FIRST_8AM', this.translate('utilities_every_month_on_the_1st')],
    ]);

    private readonly formGroup: ConsumptionAgentFormGroup = this.formBuilder.group({
        submitted: [false],
        agentPreviewed: [false],
        agentName: this.formBuilder.control<string>('', [
            Validators.maxLength(200),
            Validators.required,
            AppValidators.noEmptyStringValidator,
        ]),
        dayCount: this.formBuilder.control<number | null>(null, [
            AppValidators.numberBetween(1, 366, 'periodBetween'),
            AppValidators.integer,
            Validators.required,
        ]),
        monitoringFormGroup: this.formBuilder.group(
            {
                consumptionToMonitor: this.formBuilder.control<ConsumptionToMonitor>(
                    'INDIVIDUALCONSUMPTION',
                    Validators.required,
                ),
                selectMetersForAgentFormGroup: this.formBuilder.group({
                    submitted: [false],
                    meterSettingsFormGroup: this.formBuilder.group({
                        groupName: this.formBuilder.control<string>('', [
                            Validators.required,
                            Validators.maxLength(100),
                        ]),
                        groupName2: this.formBuilder.control<string>('', [
                            Validators.required,
                            Validators.maxLength(100),
                        ]),
                        meterSuperTypeNo: this.formBuilder.control<string>('', Validators.required),
                        outputUnit: this.formBuilder.control<string>('', Validators.required),
                        selectableUsageAndUnits: this.formBuilder.control<SelectableItem[]>(
                            [],
                            CreateConsumptionAgentComponent.atLeastOneUsageUnitValidator,
                        ),
                        selectedUsageAndUnits: this.formBuilder.control<AgentMeterType[]>([]),
                    }),
                    meterIdGroupsFormGroup: this.formBuilder.group({
                        idsInGroup1: this.formBuilder.control<number[]>(
                            [],
                            CreateConsumptionAgentComponent.oneMeterSelectedValidator,
                        ),
                        idsInGroup2: this.formBuilder.control<number[]>(
                            [],
                            CreateConsumptionAgentComponent.oneMeterSelectedValidator,
                        ),
                    }),
                }),
            },

            {validators: CreateConsumptionAgentComponent.monitoringGroupValidator},
        ),
        investigationFormGroup: this.formBuilder.group({
            agentType: this.formBuilder.control<ConsumptionAgentType>(
                'FIXED_VALUE',
                Validators.required,
            ),
            operator: this.formBuilder.control<ConsumptionAgentOperator>(
                'HIGHER',
                Validators.required,
            ),
            comparisonPeriod: this.formBuilder.control<IncreaseDecreaseAgentComparisonPeriod>(
                'PREVIOUS_PERIOD_SAME_LENGTH',
                Validators.required,
            ),
            fixedValue: this.formBuilder.control<number | null>(null, [
                Validators.min(0),
                AppValidators.preciseNumber(13, 3),
                Validators.required,
            ]),
            percentage: this.formBuilder.control<number | null>(null, {
                validators: [
                    Validators.min(0),
                    AppValidators.preciseNumber(4, 0),
                    Validators.required,
                ],
            }),
            minUnitCount: this.formBuilder.control<number | null>(null, [
                Validators.min(0),
                AppValidators.preciseNumber(13, 3),
                Validators.required,
            ]),
        }),
        recipients: this.formBuilder.control<string[]>([], Validators.required),
        schedule: this.formBuilder.control<ConsumptionAgentSchedule>('DISABLED'),
        sendBlank: this.formBuilder.control<boolean>(false),
        showConsumptionForMeters: this.formBuilder.control<boolean>(false),
    });

    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        map(buildingState =>
            buildingState.status === 'success'
                ? buildingState.buildingDetails.building.buildingNo
                : null,
        ),
        filterNulls(),
    );

    readonly view$: Observable<CreateConsumptionAgentView> = combineLatest([
        this.buildingNo$,
        fromControl(this.formGroup.controls.submitted),
    ]).pipe(
        debounceTime(0), // coalesce multiple emissions
        map(([buildingNo]) => {
            const {outputUnit} =
                this.formGroup.controls.monitoringFormGroup.getRawValue()
                    .selectMetersForAgentFormGroup.meterSettingsFormGroup;

            return {
                buildingNo,
                formGroup: this.formGroup,
                schedulerMap: this.schedulerMap,
                outputUnitTranslated:
                    outputUnit &&
                    this.translate(`mappers_measurementUnit.${outputUnit}`, 'online', 'common'),
            };
        }),
    );

    constructor() {
        const {submitted, recipients, schedule, monitoringFormGroup, investigationFormGroup} =
            this.formGroup.controls;

        // reactively set recipients to empty array when email field is disabled
        recipients.statusChanges
            .pipe(distinctUntilChanged(), takeUntilDestroyed())
            .subscribe(value => {
                if (value === 'DISABLED') {
                    recipients.setValue([], {emitEvent: false});
                }
            });

        // reactively enable/disable email field based on selected scheduler value
        fromControl(schedule)
            .pipe(takeUntilDestroyed())
            .subscribe(value => {
                const enableOrDisable = value === 'DISABLED' ? 'disable' : 'enable';
                recipients[enableOrDisable]();
            });

        const {selectMetersForAgentFormGroup} = monitoringFormGroup.controls;
        const {meterSettingsFormGroup, meterIdGroupsFormGroup} =
            selectMetersForAgentFormGroup.controls;

        // reactively enable/disable secondary meter group based on selected consumption type
        fromControl(monitoringFormGroup.controls.consumptionToMonitor)
            .pipe(
                map(consumptionToMonitor => consumptionToMonitor === 'DIFFERENCECONSUMPTION'),
                takeUntilDestroyed(),
            )
            .subscribe(compareGroups => {
                meterIdGroupsFormGroup.reset(undefined, {onlySelf: true});
                investigationFormGroup.reset(undefined, {onlySelf: true});

                this.metersForAgentService.compareGroups$.next(compareGroups);
                const {groupName2} = meterSettingsFormGroup.controls;
                const {idsInGroup2} = meterIdGroupsFormGroup.controls;
                const enableOrDisable = compareGroups ? 'enable' : 'disable';

                groupName2[enableOrDisable]();
                idsInGroup2[enableOrDisable]();
            });

        // signal changes to nested form groups explicitly
        // this is required since Angular does not properly detect changes for these components
        this.formGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            // use "onlySelf" to avoid recursion (valueChanges -> valueChanges -> valueChanges...)
            monitoringFormGroup.updateValueAndValidity({onlySelf: true});
            investigationFormGroup.updateValueAndValidity({onlySelf: true});

            // form group is not marked as dirty when nested form groups are updated
            this.formGroup.markAsDirty({emitEvent: false});

            // reset "submitted" state when user changes any form value
            submitted.setValue(false, {onlySelf: true});
            selectMetersForAgentFormGroup.controls.submitted.setValue(false, {onlySelf: true});
        });

        // emit unsaved changes state
        this.router.events
            .pipe(
                takeUntilDestroyed(),
                filter(event => event instanceof NavigationStart),
            )
            .subscribe(() => this.unsavedChanges.emit(this.hasUnsavedChanges()));
    }

    ngOnInit(): void {
        if (this.initialFormValues) {
            this.formGroup.patchValue(this.initialFormValues);
            this.formGroup.markAsPristine({emitEvent: false});
        }
    }

    ngOnDestroy(): void {
        // reset form state when component is destroyed
        this.metersForAgentService.resetState();
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (!this.hasUnsavedChanges()) {
            return;
        }

        event.preventDefault();
    }

    hasUnsavedChanges(): boolean {
        return (
            this.formGroup.dirty &&
            this.formGroup.touched &&
            this.formGroup.value.submitted === false
        );
    }

    submit(buildingNo: string): void {
        this.formGroup.markAllAsTouched();
        this.formGroup.controls.submitted.setValue(true);

        if (this.formGroup.invalid) {
            return;
        }

        const agentRequest = createUpdateAgentRequest(this.formGroup);

        // edit
        if (this.initialFormValues) {
            this.agentRequest.emit(agentRequest);
            return;
        }

        this.consumptionAgentService
            .createAgent(buildingNo, agentRequest)
            .subscribe(({agentName}) => {
                const successMessage = this.translate('monitoring_agent_created').replace(
                    '{0}',
                    agentName,
                );
                this.toastrService.success(successMessage);
                void this.router.navigate([
                    'admin',
                    'system',
                    buildingNo,
                    'monitoring',
                    'consumption-agents',
                ]);
            });
    }

    reset(): void {
        const {recipients} = this.formGroup.controls;
        recipients.setValue([]);
        this.formGroup.reset();
        this.metersForAgentService.resetState();
    }

    private static atLeastOneUsageUnitValidator({
        value,
    }: AbstractControl<SelectableItem[]>): ValidationErrors | null {
        return value.length > 0 && value.some(item => item.selected === true)
            ? null
            : {atLeastOneUsageUnit: true};
    }

    private static oneMeterSelectedValidator({
        value,
    }: AbstractControl<number[]>): ValidationErrors | null {
        return value.length ? null : {atLeastOneMeterSelected: true};
    }

    private static monitoringGroupValidator(control: AbstractControl): ValidationErrors | null {
        const createAgentFormGroup = control.parent as ConsumptionAgentFormGroup | null;
        if (!createAgentFormGroup) {
            return null;
        }

        const {submitted, agentPreviewed, monitoringFormGroup} = createAgentFormGroup.controls;
        const {selectMetersForAgentFormGroup} = monitoringFormGroup.controls;
        const selectMeterModalSubmitted = selectMetersForAgentFormGroup.value.submitted;
        if (!submitted.value && selectMeterModalSubmitted === false && !agentPreviewed.value) {
            return null;
        }

        return selectMetersForAgentFormGroup.invalid ? {atLeastOneMeterSelected: true} : null;
    }

    protected openPreviewModal(consumptionAgent: ConsumptionAgentFormGroup): void {
        this.formGroup.markAllAsTouched();
        this.formGroup.controls.agentPreviewed.setValue(true);
        if (this.formGroup.valid) {
            const modalRef = this.modalService.open(PreviewResultModalComponent, {
                size: 'xl',
                backdrop: 'static',
            });
            const component = modalRef.componentInstance as PreviewResultModalComponent;
            component.previewConsumptionAgent = mapFormGroupToConsumptionAgentTranslated(
                consumptionAgent,
                this.translateService,
            );
        }
    }
}
